import { useEffect, useRef, useState } from 'react';
import api from '../../api/axios';
import './admin.css';

function badgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'pending') return 'pending';
  if (['approved', 'succeeded', 'paid', 'accepted', 'completed'].includes(s)) return 'approved';
  if (['rejected', 'failed', 'missed', 'cancelled', 'canceled'].includes(s)) return 'rejected';
  if (s === 'refunded') return 'refunded';
  return 'default';
}

export function AdminStatusBadge({ status }) {
  return <span className={`admin-badge ${badgeClass(status)}`}>{status || '—'}</span>;
}

/**
 * Custom filter dropdown — stays within container width on mobile
 * (native <select> option popup can overflow in Chrome device mode).
 */
export function AdminFilterSelect({
  value,
  onChange,
  options = [],
  placeholder = 'All',
  className = '',
  'aria-label': ariaLabel = 'Filter',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`admin-filter-dd ${open ? 'is-open' : ''} ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className="admin-filter-dd-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="admin-filter-dd-label text-truncate">{label}</span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} />
      </button>
      {open && (
        <ul className="admin-filter-dd-menu" role="listbox">
          {options.map((opt) => (
            <li key={String(opt.value)}>
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`admin-filter-dd-option ${opt.value === value ? 'is-active' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminPageHeader({ title, subtitle, children }) {
  return (
    <div className="admin-page-head">
      <div className="admin-page-head-text min-w-0">
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {children ? <div className="admin-toolbar">{children}</div> : null}
    </div>
  );
}

export function AdminTableCard({ children }) {
  return (
    <div className="admin-card overflow-hidden">
      <div className="table-responsive">{children}</div>
    </div>
  );
}

/** Optional hook-style loader helper for list pages */
export function useAdminList(fetcher, deps = []) {
  const [data, setData] = useState({ total: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetcher();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, load, setData };
}

export { api };
